import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { Button } from 'react-bootstrap';

import { useForm } from 'react-hook-form';
import {
  FILTER_FIELD_STYLE,
  FormProvider,
  RHFDatePicker,
  RHFSelect
} from 'components/common/form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { usePackages } from 'hooks/useDonationSettings';
import {
  PLEDGE_STATUSES,
  PLEDGE_FREQUENCIES,
  PLEDGE_SOURCES,
  PLEDGE_PRIORITIES
} from './pledgeConstants';

const formSchema = Yup.object().shape({
  status: Yup.array().of(Yup.string()),
  frequency: Yup.array().of(Yup.string()),
  campaign: Yup.array().of(Yup.string()),
  source: Yup.array().of(Yup.string()),
  priority: Yup.array().of(Yup.string()),
  assigned: Yup.array().of(Yup.string()),
  followUp: Yup.object().shape({
    from: Yup.string(),
    to: Yup.string()
  })
});

const STATUS_OPTIONS = Object.entries(PLEDGE_STATUSES).map(
  ([value, { label }]) => ({ value, label })
);

const FREQUENCY_OPTIONS = Object.entries(PLEDGE_FREQUENCIES).map(
  ([value, label]) => ({ value, label })
);

const SOURCE_OPTIONS = Object.entries(PLEDGE_SOURCES).map(([value, label]) => ({
  value,
  label
}));

const PRIORITY_OPTIONS = Object.entries(PLEDGE_PRIORITIES).map(
  ([value, { label }]) => ({ value, label })
);

const DEFAULT_VALUES = {
  status: [],
  frequency: [],
  campaign: [],
  source: [],
  priority: [],
  assigned: [],
  followUp: {
    from: '',
    to: ''
  }
};

function PledgesFilterForm({ onFilter, assignedOptions, initialValues }) {
  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    // initialValues: filters from the last visit to this list (see AllPledges)
    defaultValues: { ...DEFAULT_VALUES, ...(initialValues || {}) },
    resolver: yupResolver(formSchema)
  });

  const { packages } = usePackages();

  const campaignOptions = useMemo(
    () =>
      packages?.map(({ id, package_title }) => ({
        label: package_title,
        value: id
      })) || [],
    [packages]
  );

  return (
    <FormProvider methods={form} onSubmit={form.handleSubmit(onFilter)}>
      <div className="d-flex align-items-end gap-2 flex-wrap">
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 140 }}>
          <RHFSelect
            name="status"
            label="Status"
            options={STATUS_OPTIONS}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 120 }}>
          <RHFSelect
            name="frequency"
            label="Frequency"
            options={FREQUENCY_OPTIONS}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 140 }}>
          <RHFSelect
            name="campaign"
            label="Campaign"
            options={campaignOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 110 }}>
          <RHFSelect
            name="source"
            label="Source"
            options={SOURCE_OPTIONS}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 110 }}>
          <RHFSelect
            name="priority"
            label="Priority"
            options={PRIORITY_OPTIONS}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 140 }}>
          <RHFSelect
            name="assigned"
            label="Assigned"
            options={assignedOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ flex: '0 1 110px' }}>
          <RHFDatePicker
            name="followUp.from"
            label="Follow-Up From"
            noOptional={true}
          />
        </div>
        <div style={{ flex: '0 1 110px' }}>
          <RHFDatePicker
            name="followUp.to"
            label="Follow-Up To"
            noOptional={true}
          />
        </div>
        <div className="d-flex gap-2" style={{ paddingTop: '1.5rem' }}>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              form.reset(DEFAULT_VALUES);
              form.handleSubmit(onFilter)();
            }}
          >
            Clear
          </Button>
          <Button type="submit">Filter</Button>
        </div>
      </div>
    </FormProvider>
  );
}

PledgesFilterForm.propTypes = {
  onFilter: PropTypes.func,
  assignedOptions: PropTypes.array,
  initialValues: PropTypes.object
};

export default PledgesFilterForm;
